/**
 * Picks the subtitle for a given elapsed time. The loader supports several ways of driving that
 * line and this resolves which one wins.
 *
 * In precedence order: a slow-work message once the work overruns, a stage list stepped by elapsed
 * time, an interval-cycled message list, a caller-controlled `subtitle`, and a hint that appears
 * after a short delay.
 *
 * Returns the line to show, `undefined` when the component should run its own message cycling, or an
 * empty string when there is no subtitle at all.
 */
export const resolveLoaderStatus = (
  elapsedSeconds: number,
  {
    stages,
    stageSeconds,
    slowText,
    slowAfterSeconds,
    messages,
    subtitle,
    hint,
    hintAfterSeconds,
  }: {
    stages?: string[];
    stageSeconds: number;
    slowText?: string;
    slowAfterSeconds: number;
    messages?: string[];
    subtitle?: string;
    hint?: string;
    hintAfterSeconds: number;
  },
): string | undefined => {
  if (slowText && elapsedSeconds >= slowAfterSeconds) return slowText;

  // Stages step forward with the clock and hold on the last one, so the line never loops back and
  // implies the work restarted.
  if (stages?.length) {
    const index = Math.min(Math.floor(elapsedSeconds / stageSeconds), stages.length - 1);
    return stages[index];
  }

  if (messages?.length) return undefined;

  if (subtitle) return subtitle;

  if (hint && elapsedSeconds >= hintAfterSeconds) return hint;

  return '';
};
